import { useEffect, useState, type ReactNode } from "react"

import { useFeatureFlagEnabled } from "posthog-js/react"

import {
  FEATURE_FLAGS,
  LOCAL_DEV_FLAGS,
  shouldUseLocalDevFeatureFlagOverrides,
} from "../flags"

interface FeatureFlagWrapperProps {
  flagName: string
  children: ReactNode
  // Rendered only once the flag resolves to an explicit `false` (not while
  // still loading), so a redirect fallback can't fire before the flag settles.
  fallback?: ReactNode
}

/**
 * Returns the resolved flag value. `undefined` means PostHog has not resolved
 * the flag yet, which lets callers avoid briefly rendering a fallback.
 */
export function useFeatureFlagValue(flagName: string): boolean | undefined {
  const flagEnabled = useFeatureFlagEnabled(flagName)

  // Notebooks are released to all authenticated users; server scopes enforce access.
  if (flagName === FEATURE_FLAGS.SOFIA_NOTEBOOKS) {
    return true
  }

  if (shouldUseLocalDevFeatureFlagOverrides() && flagName in LOCAL_DEV_FLAGS) {
    return LOCAL_DEV_FLAGS[flagName] === true
  }

  return flagEnabled
}

/**
 * Defers a browser-backed flag until after hydration. PostHog can synchronously
 * return a cached browser value that is unavailable to the server-rendered
 * tree, so using the raw value during the first client render can mismatch.
 */
export function usePostHydrationFeatureFlagValue(
  flagName: string,
): boolean | undefined {
  const flagValue = useFeatureFlagValue(flagName)
  const [hasHydrated, setHasHydrated] = useState(false)

  useEffect(() => {
    setHasHydrated(true)
  }, [])

  return hasHydrated ? flagValue : undefined
}

/**
 * Hook version of FeatureFlag — use when you can't wrap with the component
 * (e.g. a parent that requires direct children of a specific type).
 */
export function useIsFeatureEnabled(flagName: string): boolean {
  return useFeatureFlagValue(flagName) === true
}

/**
 * Hook to check whether the current user is in the coval-only (internal)
 * audience, backed by the `coval-only` flag — used to gate internal dev-only
 * tooling. Access is configured in PostHog (and via LOCAL_DEV_FLAGS locally),
 * not hardcoded in the frontend.
 */
export function useCovalOnly(): boolean {
  return useIsFeatureEnabled(FEATURE_FLAGS.COVAL_ONLY)
}

export const FeatureFlag = ({
  flagName,
  children,
  fallback = null,
}: FeatureFlagWrapperProps) => {
  const flagEnabled = useFeatureFlagValue(flagName)

  // Enabled -> children; explicitly disabled -> fallback; still loading -> null
  if (flagEnabled === true) {
    return <>{children}</>
  }
  if (flagEnabled === false) {
    return <>{fallback}</>
  }
  return null
}
